import { useQuery } from "@tanstack/react-query";
import { dashApi } from "@/lib/dashApi";
import { fmtDateTime, fmtRelative, fmtValue } from "@/lib/format";
import { DeltaBadge, EmptyState, ErrorState, LoadingState, PageHeader, Panel, SourceBadge, StatusPill } from "@/components/dash/ui";

export default function WhatChangedPage() {
  const q = useQuery({ queryKey: ["decisions-changes"], queryFn: dashApi.decisionsChanges });
  const d = q.data;

  return (
    <div className="space-y-6">
      <PageHeader
        title="What Changed Since Yesterday"
        description="The latest complete day versus the day before, from the IRONDash warehouse, plus alerts opened and resolved. Figures are blank when the warehouse has no history for a metric — never fabricated."
        right={d && <span className="text-xs text-slate-500">since {d.since}</span>}
      />
      {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState error={q.error} /> : d && (
        <>
          <Panel title="Day-over-day metrics">
            {!d.have_history ? (
              <EmptyState>No warehouse history available yet — daily KPI history accrues from the first rollup onward.</EmptyState>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                    <th className="pb-2 font-medium">Metric</th>
                    <th className="pb-2 text-right font-medium">Yesterday</th>
                    <th className="pb-2 text-right font-medium">Day before</th>
                    <th className="pb-2 text-right font-medium">Change</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-500/40">
                  {d.metrics.map((m) => (
                    <tr key={m.kpi} className="text-slate-300">
                      <td className="py-2">{m.label}</td>
                      <td className="py-2 text-right tabular-nums text-white">{m.current === null ? "—" : fmtValue(m.current, m.unit)}</td>
                      <td className="py-2 text-right tabular-nums text-slate-400">{m.previous === null ? "—" : fmtValue(m.previous, m.unit)}</td>
                      <td className="py-2 text-right"><DeltaBadge pct={m.change_pct} invert={m.kpi === "repairs.overdue" || m.kpi === "stock.low_stock"} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>
          <div className="grid gap-6 lg:grid-cols-2">
            <Panel title={`New alerts since yesterday (${d.new_alerts.length})`}>
              {!d.new_alerts.length ? <EmptyState compact>No new alerts.</EmptyState> : (
                <ul className="space-y-2">
                  {d.new_alerts.map((a, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <StatusPill status={a.severity} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm text-slate-200">{a.title}</p>
                        <p className="text-[11px] text-slate-500">{a.at ? fmtDateTime(a.at) : ""}</p>
                      </div>
                      {a.source && <SourceBadge code={a.source} />}
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
            <Panel title={`Resolved since yesterday (${d.resolved_alerts.length})`}>
              {!d.resolved_alerts.length ? <EmptyState compact>Nothing resolved.</EmptyState> : (
                <ul className="space-y-2">
                  {d.resolved_alerts.map((a, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm text-slate-300">{a.title}</p>
                        <p className="text-[11px] text-slate-500">{a.at ? fmtRelative(a.at) : ""}</p>
                      </div>
                      {a.source && <SourceBadge code={a.source} />}
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
          </div>
        </>
      )}
    </div>
  );
}
